import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  ApiError,
  type GameStatsRow,
  type RegisteredGame,
  type UserMatchStats,
} from '../api-client.js';
import { useUserDisplayName } from '../users/use-user-profile.js';

/**
 * Public leaderboard. Lists every user who has appeared in a
 * completed match, sorted by wins (then by win-rate as the tie-
 * breaker). Filterable by game.
 *
 * Pure client-side sort — the backend returns rows unordered so a
 * future filter (recent N days, format, etc.) won't break the
 * cache.
 */
export default function LeaderboardPage() {
  const [rows, setRows] = useState<UserMatchStats[] | null>(null);
  const [games, setGames] = useState<RegisteredGame[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    api
      .getLeaderboard()
      .then((data) => setRows(data))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    api
      .listGames()
      .then((data) => setGames(data))
      .catch(() => {});
  }, []);

  const sorted = useMemo(() => {
    if (!rows) return [];
    const filtered =
      filter === 'all'
        ? rows
        : rows
            .map((row) => projectByGame(row, filter))
            .filter((row): row is UserMatchStats => row !== null);
    return [...filtered].sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      const winRateA = a.total > 0 ? a.wins / a.total : 0;
      const winRateB = b.total > 0 ? b.wins / b.total : 0;
      if (winRateB !== winRateA) return winRateB - winRateA;
      return b.total - a.total;
    });
  }, [rows, filter]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-ec-face">Leaderboard</h2>
        <p className="font-serif text-sm text-ec-bone-dim">Top players across completed matches.</p>
        <Link to="/" className="ml-auto font-display text-sm uppercase tracking-wide text-ec-brass hover:underline">
          ← back to games
        </Link>
      </header>

      {error && (
        <div className="ec-surface p-3 text-sm text-ec-face">
          {error}
        </div>
      )}

      {!rows && !error && <p className="text-sm text-ec-bone-dim">Loading…</p>}

      {rows && games && games.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <FilterChip
            label="All games"
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          />
          {games.map((g) => (
            <FilterChip
              key={g.key}
              label={g.name}
              active={filter === g.key}
              onClick={() => setFilter(g.key)}
            />
          ))}
        </div>
      )}

      {rows && sorted.length === 0 && (
        <div className="ec-surface p-6 font-serif text-sm text-ec-bone">
          No completed matches yet. Be the first —{' '}
          <Link to="/" className="text-ec-brass hover:underline">
            start a game
          </Link>
          .
        </div>
      )}

      {rows && sorted.length > 0 && (
        <div className="ec-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ec-panel-2 text-left font-display text-xs uppercase tracking-wider text-ec-bone">
              <tr>
                <th className="px-4 py-2">#</th>
                <th className="px-4 py-2">Player</th>
                <th className="px-4 py-2 text-right">W</th>
                <th className="px-4 py-2 text-right">L</th>
                <th className="px-4 py-2 text-right">D</th>
                <th className="px-4 py-2 text-right">Total</th>
                <th className="px-4 py-2 text-right">Win %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ec-line bg-ec-ground">
              {sorted.map((row, i) => (
                <LeaderboardRow key={row.userId} row={row} rank={i + 1} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LeaderboardRow({ row, rank }: { row: UserMatchStats; rank: number }) {
  const name = useUserDisplayName(row.userId);
  const winRate = row.total > 0 ? Math.round((row.wins / row.total) * 100) : 0;
  return (
    <tr>
      <td className="px-4 py-2 font-mono text-xs text-ec-bone-dim">{rank}</td>
      <td className="px-4 py-2 text-ec-face">{name}</td>
      <td className="px-4 py-2 text-right font-display tabular-nums text-ec-go">{row.wins}</td>
      <td className="px-4 py-2 text-right font-display tabular-nums text-ec-oxblood-2">{row.losses}</td>
      <td className="px-4 py-2 text-right font-display tabular-nums text-ec-bone">{row.draws}</td>
      <td className="px-4 py-2 text-right font-display tabular-nums text-ec-bone">{row.total}</td>
      <td className="px-4 py-2 text-right font-display tabular-nums text-ec-bone">{winRate}%</td>
    </tr>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ec-btn !py-1.5 text-sm ${active ? '!border-ec-brass !text-ec-brass' : ''}`}
    >
      {label}
    </button>
  );
}

/**
 * Returns a UserMatchStats whose totals reflect only the given
 * gameKey, or null if the user has nothing in that game. Lets the
 * "All games" view share the row component without a second
 * server round-trip per filter switch.
 */
function projectByGame(row: UserMatchStats, gameKey: string): UserMatchStats | null {
  const gameRow: GameStatsRow | undefined = row.byGame.find((g) => g.gameKey === gameKey);
  if (!gameRow) return null;
  return {
    userId: row.userId,
    wins: gameRow.wins,
    losses: gameRow.losses,
    draws: gameRow.draws,
    total: gameRow.total,
    byGame: [gameRow],
  };
}
